import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { categoryFormCopy, type CategoryFormState } from '../../categoriesMeta';

type CategoryFormModalProps = {
    /** The form being edited, or `null` when closed. */
    form: CategoryFormState | null;
    locale: LocaleCode;
    isSaving: boolean;
    /** Whether a name exists in at least one language. */
    isSubmittable: boolean;
    /** The server's refusal, in the server's own words. */
    errorMessage: string | null;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    onCancel: () => void;
    onSubmit: () => void;
};

/**
 * One form for adding a category and for renaming one.
 *
 * The fields are identical and only the verb differs, so a second modal would be
 * the same twelve lines with one word changed — and two of them would drift.
 *
 * **Both languages, and neither is required on its own.** A tenant who works in
 * one language leaves the other empty and every screen falls back to the one
 * that is filled; that is a normal state for a taxonomy the tenant owns, not a
 * half-finished row. What is refused — here as a disabled button, and again on
 * the server — is the row with neither, which would render as a blank chip.
 *
 * The reader's own language leads, matching the editor's name fields: the field
 * someone is most likely to be typing into should not be the second one.
 */
export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
    form,
    locale,
    isSaving,
    isSubmittable,
    errorMessage,
    onNameKaChange,
    onNameEnChange,
    onCancel,
    onSubmit,
}) => {
    const { t } = useTranslation('pages');
    const copy = categoryFormCopy(form ?? { id: null, nameKa: '', nameEn: '' });

    const kaField = (
        <TextField
            isDisabled={isSaving}
            onChange={onNameKaChange}
            value={form?.nameKa ?? ''}
            {...getNodeTestIdProp('operation-categories-form-ka')}
        >
            <Label className="text-xs text-muted">
                {t('operations.categories.form.nameKa')}
            </Label>
            <Input
                placeholder={t('operations.categories.form.nameKaPlaceholder')}
            />
        </TextField>
    );
    const enField = (
        <TextField
            isDisabled={isSaving}
            onChange={onNameEnChange}
            value={form?.nameEn ?? ''}
            {...getNodeTestIdProp('operation-categories-form-en')}
        >
            <Label className="text-xs text-muted">
                {t('operations.categories.form.nameEn')}
            </Label>
            <Input
                placeholder={t('operations.categories.form.nameEnPlaceholder')}
            />
        </TextField>
    );

    return (
        <Modal
            isOpen={form !== null}
            onOpenChange={(open) => !open && onCancel()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-md"
                        {...getNodeTestIdProp('operation-categories-form')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>{t(copy.headingKey)}</Modal.Heading>
                            <p className="text-sm text-muted">
                                {t('operations.categories.form.subtitle')}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            {locale === 'ka' ? kaField : enField}
                            {locale === 'ka' ? enField : kaField}
                            {errorMessage ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'operation-categories-form-error',
                                    )}
                                >
                                    {errorMessage}
                                </p>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                isDisabled={isSaving}
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'operation-categories-form-cancel',
                                )}
                            >
                                {t('operations.categories.actions.cancel')}
                            </Button>
                            <Button
                                isDisabled={!isSubmittable}
                                isPending={isSaving}
                                onPress={onSubmit}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'operation-categories-form-confirm',
                                )}
                            >
                                {t(copy.confirmKey)}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
